import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ABSENCE_KINDS, ABSENCE_RULES, COMPENSATION_KINDS, documentExpected, type AbsenceKind } from '@pz/core';
import { DayPicker } from './day-picker';
import { FilePickList, type PickedFile } from './documents';
import { Button, Checkbox, ErrorBox, Field, Input, Modal, Notice, Pill, Select, useToast, type Tone } from './ui';
import { d, h, num } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';

export const LEAVE_STATUS_TONE: Record<string, Tone> = { requested: 'warn', approved: 'good', rejected: 'bad', cancelled: 'neutral' };

export function LeaveStatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  return <Pill tone={LEAVE_STATUS_TONE[status] ?? 'neutral'}>{t(`leave.status.${status as 'requested'}`)}</Pill>;
}

/** "3 Arbeitstage", "halber Tag (Vormittag)", "2 h ab 14:00" */
export function useExtentText() {
  const { t } = useTranslation();
  return (a: { from: string; to: string; halfDay: boolean; halfDayPart?: string | null; minutes?: number | null; startTime?: string | null; days: number; hours: number | null }) => {
    if (a.minutes) return `${t('leave.previewHours', { hours: num(a.minutes / 60) })}${a.startTime ? ` · ${a.startTime}` : ''}`;
    if (a.halfDay) return `${t('leave.modes.half')} (${t(`leave.parts.${(a.halfDayPart ?? 'am') as 'am'}`)})`;
    // hours of whole days come from the contract: shown only to those who may see contract hours
    if (a.hours === null) return t('leave.previewDays', { days: num(a.days) });
    return t('leave.preview', { days: num(a.days), hours: num(a.hours) });
  };
}

export function periodText(from: string, to: string) {
  return from === to ? d(from) : `${d(from)} - ${d(to)}`;
}

/** Signed hours from minutes in the app language, e.g. "+12,50" or "-4,00". */
export function signedHours(minutes: number) {
  if (Math.round(minutes) === 0) return h(0);
  return `${minutes > 0 ? '+' : '-'}${h(Math.abs(minutes))}`;
}

/** request: leave (needs approval); sick: sick notes; any: every kind the person can enter (week view); hr: direct entry by HR */
type Mode = 'request' | 'sick' | 'any' | 'hr';

/**
 * Request (person), sick report (person) or direct entry (HR) of an absence, with a live preview of the working
 * days, the remaining leave and who decides. Documents (sick note, registration, invitation) can be attached right
 * away; they are uploaded after the request is stored.
 */
export function LeaveDialog({ open, mode, date, initial, onClose, onDone }: { open: boolean; mode: Mode; date?: string | null; initial?: { id?: string; kind: string; from: string; to: string; note?: string | null } | null; onClose: () => void; onDone?: () => void }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const kinds = ABSENCE_KINDS.filter((k) =>
    mode === 'hr' ? true : mode === 'sick' ? ABSENCE_RULES[k].health : mode === 'any' ? ABSENCE_RULES[k].selfService : ABSENCE_RULES[k].selfService && !ABSENCE_RULES[k].health,
  );
  const [personId, setPersonId] = useState('');
  const [kind, setKind] = useState<AbsenceKind>(kinds[0]);
  const [from, setFrom] = useState(me.today);
  const [to, setTo] = useState(me.today);
  const [extent, setExtent] = useState<'full' | 'half' | 'hours'>('full');
  const [part, setPart] = useState<'am' | 'pm'>('am');
  const [hours, setHours] = useState('2');
  const [start, setStart] = useState('08:00');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<PickedFile[]>([]);
  // a range (from - to) or days chosen one by one in the calendar (request 24 of round 4)
  // round 5 (request 26): the calendar first; the range stays for long absences, half days and hours
  const [pick, setPick] = useState<'range' | 'days'>('days');
  const [dates, setDates] = useState<Set<string>>(new Set());
  // HR: a sickness on days of leave cuts the leave (the leave days go back)
  const [cutLeave, setCutLeave] = useState(false);
  useEffect(() => {
    if (!open) return;
    setFiles([]);
    // a request sent back for a change keeps its range; a new one starts with the calendar
    setPick(initial?.from && initial.from !== initial.to ? 'range' : 'days');
    setDates(new Set());
    setCutLeave(false);
    // "adjust and resubmit": the dates and the kind of the request the approver sent back
    setKind(initial && (kinds as string[]).includes(initial.kind) ? (initial.kind as AbsenceKind) : kinds[0]);
    setFrom(initial?.from ?? date ?? me.today);
    setTo(initial?.to ?? date ?? me.today);
    setExtent('full');
    setPart('am');
    setHours('2');
    setStart('08:00');
    setNote(initial?.note ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date, mode, initial]);
  const single = from === to;
  const minutes = extent === 'hours' && single ? Math.round(Number(hours.replace(',', '.')) * 4) * 15 : null;
  const input = {
    kind,
    from,
    to,
    halfDay: extent === 'half' && single,
    halfDayPart: extent === 'half' && single ? part : null,
    minutes: minutes && minutes > 0 ? minutes : null,
    startTime: extent === 'hours' && single ? start : null,
    note: note.trim() || null,
  };
  const days = pick === 'days';
  const dateList = [...dates].sort();
  const valid = days
    ? dateList.length > 0 && (mode !== 'hr' || !!personId)
    : !!from && !!to && from <= to && (mode !== 'hr' || !!personId) && (extent !== 'hours' || (!!input.minutes && input.minutes <= 720));
  const people = useQ('people.list', {}, { enabled: open && mode === 'hr' });
  const forPerson = mode === 'hr' ? personId || undefined : undefined;
  const preview = useQ('leave.preview', { ...input, personId: forPerson }, { enabled: open && valid && !days });
  const previewDays = useQ('leave.previewDays', { kind, dates: dateList, personId: forPerson }, { enabled: open && valid && days });
  const invalidate = ['leave.', 'absences.', 'time.', 'kpi.', 'workload.', 'dashboard.', 'people.'];
  const request = useM('leave.request', { invalidate });
  const requestDays = useM('leave.requestDays', { invalidate });
  const create = useM('absences.create', { invalidate });
  const createDays = useM('absences.createDays', { invalidate });
  const addDocument = useM('leave.addDocument', { invalidate });
  const busy = request.isPending || create.isPending || requestDays.isPending || createDays.isPending || addDocument.isPending;
  const submit = async () => {
    let r: { id: string; status?: string };
    try {
      if (days) {
        r =
          mode === 'hr'
            ? await createDays.mutateAsync({ personId, kind, dates: dateList, note: input.note, cutLeave })
            : await requestDays.mutateAsync({ kind, dates: dateList, note: input.note });
      } else {
        r = mode === 'hr' ? await create.mutateAsync({ ...input, personId, cutLeave }) : await request.mutateAsync({ ...input, replaces: initial?.id });
      }
    } catch {
      return;
    }
    let uploaded = 0;
    for (const f of files) {
      try {
        await addDocument.mutateAsync({ absenceId: r.id, fileName: f.name, mime: f.mime, base64: f.base64 });
        uploaded++;
      } catch {
        /* reported below */
      }
    }
    toast.ok(mode !== 'hr' && ABSENCE_RULES[kind].health ? t('leave.sickRecorded') : r.status === 'requested' ? t('leave.requested') : t('leave.recorded'));
    if (uploaded < files.length) toast.warn(t('documents.uploadFailed', { count: files.length - uploaded }));
    onDone?.();
    onClose();
  };
  const pvRange = preview.data;
  const pvDays = previewDays.data;
  const pv = days ? pvDays : pvRange;
  // the cut of leave by a sickness, in the range and (round 5, R-10) in the calendar
  const leaveCut = (days ? pvDays?.leaveCut : pvRange?.leaveCut) ?? null;
  // HR: a sickness on days of leave goes in only together with the cut of the leave
  const blocked = !!pv?.overlap && !(mode === 'hr' && leaveCut && cutLeave);
  const needsApproval = mode !== 'hr' && ABSENCE_RULES[kind].approval;
  const health = ABSENCE_RULES[kind].health;
  const docRequired = health && !!from && !!to && from <= to && documentExpected(kind, from, to, me.settings.leaveDocumentFromDay);
  const docDay = kind === 'child_sick' ? me.settings.leaveDocumentFromDay.child_sick : me.settings.leaveDocumentFromDay.sick;
  // who decides a request (sick reports: always HR, said in sickInfo)
  const approverText = me.leaveApproverName ? t('leave.decidedByName', { name: me.leaveApproverName }) : t('leave.decidedByHr');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'any' ? t('week.markAbsence') : t(mode === 'hr' ? 'leave.hrTitle' : mode === 'sick' ? 'leave.sickTitle' : 'leave.requestTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!valid || blocked} loading={busy} onClick={() => void submit()}>
            {t(needsApproval && !health ? 'leave.submit' : 'leave.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mode === 'hr' && (
          <Field label={t('leave.person')}>
            <Select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">{t('common.select')}</option>
              {(people.data?.people ?? [])
                .filter((p) => p.kind === 'employee')
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        {kinds.length > 1 && (
          <Field label={t('leave.kind')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as AbsenceKind)}>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {t(`week.absence.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {!initial?.id && (
          <div className="flex w-fit rounded-md border border-line p-0.5" role="group" aria-label={t('days.mode')}>
            {(['days', 'range'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={pick === m}
                onClick={() => setPick(m)}
                className={pick === m ? 'rounded bg-accent px-3 py-1 text-sm text-white' : 'rounded px-3 py-1 text-sm text-graphite hover:bg-sheet-2 hover:text-ink'}
              >
                {t(`days.modes.${m}`)}
              </button>
            ))}
          </div>
        )}
        {days ? (
          <>
            <DayPicker personId={forPerson} value={dates} onChange={setDates} overLeave={mode === 'hr' && kind === 'sick'} />
            <p className="-mt-2 text-xs text-graphite">{t('days.hint')}</p>
          </>
        ) : (
          <div className="flex flex-wrap gap-4">
            <Field label={t('common.from')} htmlFor="leave-from">
              <Input id="leave-from" type="date" value={from} onChange={(e) => (setFrom(e.target.value), e.target.value > to && setTo(e.target.value))} className="w-40" />
            </Field>
            <Field label={t('common.to')} htmlFor="leave-to">
              <Input id="leave-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="w-40" />
            </Field>
          </div>
        )}
        {single && !days && (
          <div className="flex flex-wrap items-end gap-4">
            <Field label={t('leave.extent')}>
              <Select value={extent} onChange={(e) => setExtent(e.target.value as 'full')} className="w-40">
                {(['full', 'half', 'hours'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`leave.modes.${m}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {extent === 'half' && (
              <Field label={'\u00a0'}>
                <Select value={part} onChange={(e) => setPart(e.target.value as 'am')} className="w-40">
                  <option value="am">{t('leave.parts.am')}</option>
                  <option value="pm">{t('leave.parts.pm')}</option>
                </Select>
              </Field>
            )}
            {extent === 'hours' && (
              <>
                <Field label={t('leave.hours')} htmlFor="leave-hours">
                  <Input id="leave-hours" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} className="num w-24" />
                </Field>
                <Field label={t('leave.startTime')} htmlFor="leave-start">
                  <Input id="leave-start" type="time" step={900} value={start} onChange={(e) => setStart(e.target.value)} className="w-28" />
                </Field>
              </>
            )}
          </div>
        )}
        <Field label={t('leave.note')} hint={t('leave.noteHint')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </Field>
        {pv && (
          <div className="flex flex-col gap-1 rounded-md border border-line bg-sheet-2 px-3 py-2 text-sm">
            <span className="num font-medium">{t('leave.preview', { days: num(pv.days), hours: num(pv.minutes / 60) })}</span>
            {days && pvDays && pvDays.spans.length > 1 && <span className="text-xs text-graphite">{t('days.records', { count: pvDays.spans.length })}</span>}
            {days && pvDays && pvDays.ignored.length > 0 && <span className="text-xs text-warn">{t('days.ignored', { count: pvDays.ignored.length })}</span>}
            {pv.balances.map((b) => (
              <span key={b.year} className={b.value < 0 ? 'text-bad' : 'text-graphite'}>
                {pv.balances.length > 1
                  ? t('leave.balanceAfterYear', { year: b.year, value: num(b.value), unit: t(`leave.units.${pv.unit}`) })
                  : t('leave.balanceAfter', { value: num(b.value), unit: t(`leave.units.${pv.unit}`) })}
              </span>
            ))}
            {!days && pvRange && pvRange.overtimeAfterMinutes !== null && (
              <span className={pvRange.overtimeAfterMinutes < 0 ? 'text-bad' : 'text-graphite'}>{t('leave.overtimeAfter', { hours: signedHours(pvRange.overtimeAfterMinutes) })}</span>
            )}
          </div>
        )}
        <Field
          label={docRequired ? t('documents.labelRequired') : t('documents.labelOptional')}
          hint={health ? t('documents.healthHint', { day: docDay }) : t('documents.otherHint')}
        >
          <FilePickList files={files} onChange={setFiles} />
        </Field>
        {docRequired && files.length === 0 && <Notice tone="warn">{t('documents.requiredNotice', { day: docDay })}</Notice>}
        {pv?.overlap && leaveCut && mode === 'hr' && (
          <Notice tone="warn">
            <span className="block">{t('leave.cut.text', { days: num(leaveCut.days) })}</span>
            <Checkbox className="mt-2" label={t('leave.cut.confirm', { days: num(leaveCut.days) })} checked={cutLeave} onChange={(e) => setCutLeave(e.target.checked)} />
          </Notice>
        )}
        {pv?.overlap && leaveCut && mode !== 'hr' && <Notice tone="warn">{t('leave.cut.self')}</Notice>}
        {pv?.overlap && !leaveCut && <Notice tone="bad">{t('leave.overlap')}</Notice>}
        {!!pv?.bookedMinutes && !health && <Notice tone="warn">{t('leave.bookedWarning', { hours: num(pv.bookedMinutes / 60) })}</Notice>}
        {mode !== 'hr' && (
          <Notice tone="neutral">
            {health ? t('leave.sickInfo') : COMPENSATION_KINDS.includes(kind) ? t('leave.compInfo') : t('leave.needsApproval')} {needsApproval && !health && approverText}
          </Notice>
        )}
        {mode === 'hr' && <Notice tone="neutral">{t('leave.hrInfo')}</Notice>}
        <ErrorBox error={request.error ?? create.error ?? requestDays.error ?? createDays.error} />
      </div>
    </Modal>
  );
}
